import {
  showMessage,
  SNACKBAR_MESSAGE_SEVERITY_SUCCESS,
  SNACKBAR_MESSAGE_SEVERITY_WARNING
} from "../../hocs/snackbar-provider";
import {getActionExtras} from "../action-decorator/middlewareFactory";
import {REQUEST_ERROR, REQUEST_SUCCESS} from "../request/requestActions";
import {
  createPackage,
  createResource,
  PACKAGE_MANAGER_MIDDLEWARE_PACKAGE_CREATE,
  PACKAGE_MANAGER_MIDDLEWARE_RESOURCE_CREATE
} from "./actions";
import {clearCatalog} from "../../state/catalog/catalogActions";
import {
  fetchPackageList,
  PACKAGE_MANAGER_EASY_LOADING_ORGANIZATIONS_GET,
  PACKAGE_MANAGER_EASY_LOADING_PACKAGE_LICENSE_GET,
  PACKAGE_MANAGER_EASY_LOADING_RESOURCE_LICENSE_GET,
  PACKAGE_MANAGER_EASY_LOADING_SUBMIT,
  PACKAGE_MANAGER_EASY_LOADING_THEMES_GET,
  PACKAGE_MANAGER_EASY_LOADING_THEMES_SPINNER,
  PACKAGE_MANAGER_ETL_WIZARD_MODAL_SUBMIT,
  PACKAGE_MANAGER_ETL_WIZARD_REPORT_HIDE,
  PACKAGE_MANAGER_ETL_WIZARD_RESOURCE_DELETE,
  PACKAGE_MANAGER_PACKAGE_APPROVE,
  PACKAGE_MANAGER_PACKAGE_DISAPPROVE,
  PACKAGE_MANAGER_PACKAGE_LIST_HIDE,
  PACKAGE_MANAGER_SUPERSET_DEPENDENCIES_GET,
  reloadCatalog,
  submitEasyLoadingError,
  submitEasyLoadingSuccess
} from "../../state/package-manager/packageManagerActions";
import {markSpinnerMessage} from "../../state/spinner/spinnerActions";
import EXTRAS from "../../utils/extras";

const middlewarePackageManager =
  ({getState, dispatch}) =>
  next =>
  action => {
    switch (action.type) {
      case PACKAGE_MANAGER_PACKAGE_LIST_HIDE: {
        const state = getState();
        if (state.packageManager?.reloadCatalog && state?.node?.nodeId === state.packageManager?.nodeId) {
          dispatch(clearCatalog());
        }
        break;
      }
      case PACKAGE_MANAGER_ETL_WIZARD_REPORT_HIDE: {
        const state = getState();
        const importResult = state.packageManager?.etl?.importResult;
        if (importResult?.dataImported) {
          if (state.packageManager?.isPackageListModalVisible) {
            dispatch(fetchPackageList(state.packageManager?.nodeId));
            dispatch(reloadCatalog());
          } else {
            dispatch(clearCatalog());
          }
        }
        break;
      }
      case PACKAGE_MANAGER_EASY_LOADING_SUBMIT: {
        const state = getState();
        const language = state.app.language;
        const nodeId = action.payload.nodeId;
        const nodeType = action.payload.nodeType;
        const dataset = action.payload.dataset;
        const resource = action.payload.resource;

        dispatch(createPackage(nodeId, nodeType, dataset, resource, language));
        break;
      }
      case REQUEST_SUCCESS: {
        switch (action.payload.label) {
          case PACKAGE_MANAGER_MIDDLEWARE_PACKAGE_CREATE: {
            const state = getState();
            const language = state.app.language;
            const nodeId = action.payload.extra.nodeId;
            const nodeType = action.payload.extra.nodeType;
            const resource = action.payload.extra.resource;

            const packageId = action.payload.response?.identifier || "";

            resource.packageId = packageId;

            dispatch(createResource(nodeId, nodeType, resource, packageId, language));
            break;
          }
          case PACKAGE_MANAGER_MIDDLEWARE_RESOURCE_CREATE: {
            const packageId = action.payload.extra.packageId;
            const resourceId = action.payload.response?.identifier || "";

            dispatch(submitEasyLoadingSuccess(packageId, resourceId));
            break;
          }
          case PACKAGE_MANAGER_SUPERSET_DEPENDENCIES_GET: {
            const hasDependencies = action.payload.response?.HasDependencies || false;
            if (hasDependencies) {
              if (action.payload.extra?.onDependenciesFound) {
                action.payload.extra.onDependenciesFound(action.payload.response || {});
              }
            } else {
              if (action.payload.extra?.onNoDependencies) {
                action.payload.extra.onNoDependencies(action.payload.response || {});
              }
            }
            break;
          }
          case PACKAGE_MANAGER_ETL_WIZARD_MODAL_SUBMIT: {
            const importResult = action.payload.response;
            const {t} = getActionExtras(action);
            if (
              importResult.dataImported &&
              importResult.supersetSynchronized !== null &&
              importResult.supersetSynchronized !== undefined
            ) {
              if (importResult.supersetSynchronized) {
                showMessage(
                  t("modules.packageManager.actions.messages.supersetSynchronized.submit.success"),
                  SNACKBAR_MESSAGE_SEVERITY_SUCCESS
                );
              } else {
                showMessage(
                  t("modules.packageManager.actions.messages.supersetSynchronized.submit.failed"),
                  SNACKBAR_MESSAGE_SEVERITY_WARNING
                );
              }
            }
            break;
          }
          case PACKAGE_MANAGER_ETL_WIZARD_RESOURCE_DELETE: {
            const response = action.payload.response;
            const {t} = getActionExtras(action);

            const state = getState();
            const nodes = state?.nodesConfig?.nodes;
            const selectedNode = (nodes || []).find(node => node.nodeId === action.payload.extra.nodeId);
            const selectedNodeExtras = EXTRAS.parse(selectedNode?.extras);

            if ((response?.conflicts || []).length > 0) {
              if (response.conflicts.some(conflict => conflict.includes("DELETE_SUPERSET_DATASET"))) {
                showMessage(
                  t("modules.packageManager.actions.messages.supersetSynchronized.delete.failed"),
                  SNACKBAR_MESSAGE_SEVERITY_WARNING
                );
              } else if (selectedNodeExtras.SupersetEnabled) {
                showMessage(
                  t("modules.packageManager.actions.messages.supersetSynchronized.delete.success"),
                  SNACKBAR_MESSAGE_SEVERITY_SUCCESS
                );
              }
            } else {
              if (selectedNodeExtras.SupersetEnabled) {
                showMessage(
                  t("modules.packageManager.actions.messages.supersetSynchronized.delete.success"),
                  SNACKBAR_MESSAGE_SEVERITY_SUCCESS
                );
              }
            }

            if (action.payload.extra.onDeleteEnd) {
              action.payload.extra.onDeleteEnd();
            } else {
              const state = getState();
              const nodeId = state.packageManager?.nodeId;
              dispatch(fetchPackageList(nodeId));
              dispatch(reloadCatalog());
            }
            break;
          }
          case PACKAGE_MANAGER_PACKAGE_APPROVE:
          case PACKAGE_MANAGER_PACKAGE_DISAPPROVE: {
            const state = getState();
            if (state.packageManager?.isPackageListModalVisible) {
              dispatch(reloadCatalog());
            } else {
              dispatch(clearCatalog());
            }
            break;
          }
          default:
            break;
        }
        break;
      }
      case REQUEST_ERROR: {
        switch (action.payload.label) {
          case PACKAGE_MANAGER_MIDDLEWARE_PACKAGE_CREATE:
          case PACKAGE_MANAGER_MIDDLEWARE_RESOURCE_CREATE: {
            dispatch(submitEasyLoadingError());
            break;
          }
          case PACKAGE_MANAGER_SUPERSET_DEPENDENCIES_GET: {
            if (action.payload.extra?.onNoDependencies) {
              action.payload.extra.onNoDependencies();
            }
            break;
          }
          case PACKAGE_MANAGER_EASY_LOADING_THEMES_GET:
          case PACKAGE_MANAGER_EASY_LOADING_ORGANIZATIONS_GET:
          case PACKAGE_MANAGER_EASY_LOADING_PACKAGE_LICENSE_GET:
          case PACKAGE_MANAGER_EASY_LOADING_RESOURCE_LICENSE_GET: {
            dispatch(markSpinnerMessage(PACKAGE_MANAGER_EASY_LOADING_THEMES_SPINNER, true));
            break;
          }
          default:
            break;
        }
        break;
      }
      default:
        break;
    }

    return next(action);
  };

export default middlewarePackageManager;
